/**
 * AIBriefingDraftPanel — Iter-411.
 *
 * Panel that lets a client or admin:
 *   1. Generate an AI draft briefing for a shift (POST /api/ai-briefing/{shift_id}/draft)
 *   2. Review each section in editable inputs
 *   3. Save partial edits (PATCH .../draft)
 *   4. Approve the draft, merging it into shifts.event_briefing (POST .../approve)
 *   5. Reject the draft (POST .../reject)
 *   6. Inspect full version history (GET .../versions)
 *
 * Nothing is pushed to talent until the human approves — approval is
 * explicit and always a separate action.
 *
 * The active LLM model is selected server-side by env var
 * (LLM_BRIEFING_PROVIDER / LLM_BRIEFING_MODEL), so this component never
 * mentions Claude / GPT specifically.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Sparkles, Loader2, CheckCircle2, XCircle, History, Save,
  AlertTriangle, RefreshCcw,
} from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const SECTIONS = [
  { key: 'event_overview',       label: 'Event Overview',       lines: 3 },
  { key: 'responsibilities',     label: 'Responsibilities',     lines: 4 },
  { key: 'dress_code',           label: 'Dress Code',           lines: 3 },
  { key: 'parking_info',         label: 'Parking Info',         lines: 2 },
  { key: 'arrival_instructions', label: 'Arrival Instructions', lines: 3 },
  { key: 'contact_name',         label: 'Onsite Contact Name',  lines: 1 },
  { key: 'contact_phone',        label: 'Onsite Contact Phone', lines: 1 },
  { key: 'run_of_show',          label: 'Run of Show',          lines: 4 },
  { key: 'product_info',         label: 'Product Info',         lines: 3 },
  { key: 'important_notes',      label: 'Important Notes',      lines: 3 },
];

const NEEDS_INPUT_RE = /\[NEEDS INPUT FROM CLIENT\]/i;

export default function AIBriefingDraftPanel({ shiftId, onApproved }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [askingRejectReason, setAskingRejectReason] = useState(false);

  const loadDraft = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/ai-briefing/${shiftId}/draft`);
      setDraft(res.data?.has_draft ? res.data.draft : null);
    } catch (e) {
      // silent — no draft yet is fine
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { loadDraft(); }, [loadDraft]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/api/ai-briefing/${shiftId}/draft`);
      setDraft(res.data.draft);
      toast.success('AI draft generated. Review before approving.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Draft generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const updateField = (key, value) => {
    setDraft(prev => prev
      ? { ...prev, content: { ...(prev.content || {}), [key]: value } }
      : prev
    );
  };

  const saveEdits = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await api.patch(`/api/ai-briefing/${shiftId}/draft`, {
        content: draft.content,
        diff_summary: 'Human edit',
      });
      setDraft(res.data.draft);
      toast.success('Draft saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/api/ai-briefing/${shiftId}/approve`);
      toast.success('Briefing approved and applied to the shift');
      setDraft(null);
      onApproved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    setRejecting(true);
    try {
      await api.post(`/api/ai-briefing/${shiftId}/reject`, { reason: rejectReason || null });
      toast.success('Draft rejected');
      setDraft(null);
      setAskingRejectReason(false);
      setRejectReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally {
      setRejecting(false);
    }
  };

  const loadVersions = async () => {
    setShowVersions(true);
    try {
      const res = await api.get(`/api/ai-briefing/${shiftId}/versions`);
      setVersions(res.data.versions || []);
    } catch (e) {
      toast.error('Failed to load version history');
    }
  };

  const gapsCount = draft?.content
    ? Object.values(draft.content).filter(v =>
        typeof v === 'string' && NEEDS_INPUT_RE.test(v)
      ).length
    : 0;

  return (
    <div
      data-testid="ai-briefing-draft-panel"
      className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-slate-200"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-teal-300">
            <Sparkles className="w-3.5 h-3.5" />
            AI Draft Briefing
          </div>
          <h3 className="text-lg font-semibold text-white mt-1">
            Draft, review, then approve — nothing goes to talent until you do.
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {draft && (
            <button
              type="button"
              onClick={loadVersions}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-900/60 hover:bg-slate-800 px-3 py-1.5 text-xs text-slate-300"
              data-testid="briefing-versions-btn"
            >
              <History className="w-3.5 h-3.5" />
              Versions
            </button>
          )}
          <button
            type="button"
            onClick={generate}
            disabled={generating || loading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 disabled:opacity-60 px-3 py-1.5 text-xs font-medium text-slate-950"
            data-testid="briefing-generate-btn"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            {draft ? 'Regenerate' : 'Generate AI Draft'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-slate-400 text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading current draft…
        </div>
      )}

      {!loading && !draft && (
        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-5 text-center text-sm text-slate-400">
          No active draft yet. Click <b className="text-teal-300">Generate AI Draft</b> to create one.
        </div>
      )}

      {draft && (
        <>
          {gapsCount > 0 && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {gapsCount} section{gapsCount === 1 ? '' : 's'} still say
                <code className="mx-1 px-1 py-0.5 rounded bg-amber-500/20 text-amber-100">[NEEDS INPUT FROM CLIENT]</code>.
                Fill them in before approving so talent see real information.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SECTIONS.map(sec => {
              const value = draft.content?.[sec.key] || '';
              const isGap = typeof value === 'string' && NEEDS_INPUT_RE.test(value);
              return (
                <div key={sec.key} className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                  <label className="flex items-center justify-between text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">
                    <span>{sec.label}</span>
                    {isGap && <span className="text-amber-300">needs input</span>}
                  </label>
                  {sec.lines === 1 ? (
                    <input
                      type="text"
                      className={`w-full rounded-md bg-slate-950 border ${isGap ? 'border-amber-500/40' : 'border-slate-700'} px-2 py-1.5 text-sm text-slate-100`}
                      value={value}
                      onChange={e => updateField(sec.key, e.target.value)}
                      data-testid={`briefing-field-${sec.key}`}
                    />
                  ) : (
                    <textarea
                      rows={sec.lines}
                      className={`w-full rounded-md bg-slate-950 border ${isGap ? 'border-amber-500/40' : 'border-slate-700'} px-2 py-1.5 text-sm text-slate-100 resize-y`}
                      value={value}
                      onChange={e => updateField(sec.key, e.target.value)}
                      data-testid={`briefing-field-${sec.key}`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              Version&nbsp;
              <span className="text-slate-300 font-mono">{draft.current_version || 1}</span>
              &nbsp;·&nbsp;{draft.provider || '—'}/{draft.model || '—'}
              &nbsp;·&nbsp;status <span className="text-teal-300 font-mono">{draft.status}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={saveEdits}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 bg-slate-800 hover:bg-slate-700 px-3 py-2 text-xs font-medium text-slate-100 disabled:opacity-60"
                data-testid="briefing-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save edits
              </button>
              <button
                type="button"
                onClick={() => setAskingRejectReason(true)}
                disabled={rejecting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 px-3 py-2 text-xs font-medium text-rose-200 disabled:opacity-60"
                data-testid="briefing-reject-btn"
              >
                <XCircle className="w-3.5 h-3.5" />
                Reject
              </button>
              <button
                type="button"
                onClick={approve}
                disabled={approving || gapsCount > 0}
                title={gapsCount > 0 ? 'Fill in the gaps first' : 'Approve and apply'}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 px-3 py-2 text-xs font-semibold text-slate-950"
                data-testid="briefing-approve-btn"
              >
                {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Approve &amp; apply
              </button>
            </div>
          </div>
        </>
      )}

      {askingRejectReason && (
        <div className="mt-4 rounded-xl border border-rose-500/40 bg-rose-500/10 p-4">
          <label className="block text-xs font-medium text-rose-200 mb-1">Rejection reason (optional)</label>
          <textarea
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-2 py-1.5 text-sm text-slate-100"
            rows={2}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            data-testid="briefing-reject-reason"
          />
          <div className="mt-2 flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => setAskingRejectReason(false)}
              className="rounded-lg px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={reject}
              disabled={rejecting}
              className="rounded-lg bg-rose-500 hover:bg-rose-400 px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-60"
              data-testid="briefing-reject-confirm"
            >
              {rejecting ? 'Rejecting…' : 'Confirm reject'}
            </button>
          </div>
        </div>
      )}

      {showVersions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-950 p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold flex items-center gap-2">
                <History className="w-4 h-4" /> Version History
              </h4>
              <button
                onClick={() => setShowVersions(false)}
                className="text-slate-400 hover:text-white text-sm"
                data-testid="briefing-versions-close"
              >
                Close
              </button>
            </div>
            {versions.length === 0 && (
              <div className="text-sm text-slate-400">No versions recorded yet.</div>
            )}
            {versions.map(v => (
              <div key={v.id} className="mb-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-300">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-teal-300">v{v.version_number} · {v.action}</span>
                  <span className="text-slate-500">{v.created_at ? new Date(v.created_at).toLocaleString() : ''}</span>
                </div>
                <div className="text-slate-400">{v.diff_summary || '(no summary)'}</div>
                <div className="text-slate-500 mt-1">
                  {v.actor_role || 'system'}
                  {v.provider ? ` · ${v.provider}/${v.model}` : ''}
                  {v.latency_ms ? ` · ${v.latency_ms}ms` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
